import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useLocale } from '../contexts/LocaleContext'
import { siteConfig, getAffiliateLink } from '../config/site'
import { updateSEO } from '../utils/seo'
import { loadArticle } from '../utils/loadArticle'
import { markdownToHtml } from '../utils/markdownToHtml'
import ReviewCard from '../components/ReviewCard'
import ComparisonTable from '../components/ComparisonTable'
import './ArticlePage.css'

const SegmentPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { locale, t } = useLocale()
  const slug = location.pathname.split('/').pop()
  const [pageData, setPageData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true)
      const data = await loadArticle(slug, locale, 'page')
      setPageData(data)
      
      if (data) {
        updateSEO({
          title: data.metaTitle,
          description: data.metaDescription,
          keywords: data.metaKeywords,
          url: `${siteConfig.baseUrl}/${slug}`
        })
      }
      setLoading(false)
    }
    loadPage()
  }, [slug, locale])

  if (loading) {
    return <div className="article-page-container"><div className="loading">{t('pages.loading')}</div></div>
  }

  if (!pageData) {
    navigate('/')
    return null
  }

  return (
    <div className="article-page-container">
      <button className="back-button" onClick={() => navigate('/')}>
        ← {t('pages.backToHome')}
      </button>

      <div className="article-content">
        <h1 className="article-title">{pageData.title}</h1>
        
        {pageData.introduction && (
          <div className="article-section" dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.introduction) }} />
        )}
        
        {pageData.reviews && Array.isArray(pageData.reviews) && (
          <div className="article-section">
            <h2>{t('pages.segment.sectionTitles.appReviews')}</h2>
            {pageData.reviews.map((review, i) => (
              <ReviewCard key={i} review={review} />
            ))}
          </div>
        )}
        
        {pageData.comparison_table && typeof pageData.comparison_table === 'object' && (
          <div className="article-section">
            <h2>Vergleichstabelle</h2>
            <ComparisonTable table={pageData.comparison_table} />
          </div>
        )}
        
        {pageData.comparison_table && typeof pageData.comparison_table === 'string' && (
          <div className="article-section" dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.comparison_table) }} />
        )}
        
        {pageData.content && (
          <div className="article-section" dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.content) }} />
        )}
        
        {pageData.guide && (
          <div className="article-section" dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.guide) }} />
        )}
        
        {pageData.tips && typeof pageData.tips === 'object' && (
          <div className="article-section">
            {pageData.tips.title && <h2>{pageData.tips.title}</h2>}
            {pageData.tips.content && (
              <div dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.tips.content) }} />
            )}
          </div>
        )}
        
        {pageData.tips && typeof pageData.tips === 'string' && (
          <div className="article-section" dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.tips) }} />
        )}
        
        {pageData.platforms && typeof pageData.platforms === 'object' && (
          <div className="article-section">
            {pageData.platforms.title && <h2>{pageData.platforms.title}</h2>}
            {pageData.platforms.content && (
              <div dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.platforms.content) }} />
            )}
            {pageData.platforms.platformsList && Array.isArray(pageData.platforms.platformsList) && (
              <ul className="platforms-list">
                {pageData.platforms.platformsList.map((platform, i) => (
                  <li key={i} dangerouslySetInnerHTML={{ __html: markdownToHtml(platform) }} />
                ))}
              </ul>
            )}
          </div>
        )}
        
        {pageData.platforms && typeof pageData.platforms === 'string' && (
          <div className="article-section" dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.platforms) }} />
        )}
        
        {pageData.rankings && Array.isArray(pageData.rankings) && (
          <div className="article-section">
            <h2>{t('pages.segment.sectionTitles.topRankings')}</h2>
            {pageData.rankings.map((ranking, i) => (
              <div key={i} className="ranking-item" dangerouslySetInnerHTML={{ __html: markdownToHtml(ranking) }} />
            ))}
          </div>
        )}
        
        {pageData.faq && (
          <div className="article-section faq-section">
            <h2>{pageData.faq.title || t('pages.segment.sectionTitles.faq')}</h2>
            <div className="faq-list">
              {/* Handle both structures: items and questions */}
              {(pageData.faq.items || pageData.faq.questions || []).map((item, i) => (
                <div key={i} className="faq-item">
                  <h3 className="faq-question">{item.question}</h3>
                  <p className="faq-answer">{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {pageData.conclusion && (
          <div className="article-section" dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.conclusion) }} />
        )}
        
        <div className="affiliate-cta">
          <a href={getAffiliateLink()} target="_blank" rel="noopener noreferrer" className="affiliate-button">
            {t('pages.segment.affiliateCTA')}
          </a>
        </div>
      </div>
    </div>
  )
}

export default SegmentPage

